import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Key, Trash2, Copy, ToggleLeft, ToggleRight, AlertTriangle, Code2, Loader2, X, Check } from 'lucide-react';
import { AdminAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Onglet Partner API — gestion des clés permettant à des partenaires externes
 * (marketplaces, PMS dentaires, comparateurs, mobile apps) d'accéder au catalogue
 * via `X-API-Key`. Endpoints exposés : /api/partner/products|categories|brands|me.
 *
 * Sécurité : la clé en clair est affichée UNE SEULE FOIS à la création (modal),
 * puis seul le hash SHA-256 est stocké en DB. Impossible de la récupérer après.
 */
const SCOPES_AVAILABLE = [
  { value: 'products:read', label: 'Lecture produits' },
  { value: 'categories:read', label: 'Lecture catégories' },
  { value: 'brands:read', label: 'Lecture marques' },
];

const PartnerKeysTab = () => {
  const { toast } = useToast();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false); // modal create
  const [form, setForm] = useState({
    name: '', scopes: SCOPES_AVAILABLE.map(s => s.value), rate_limit_per_minute: 60,
    partner_email: '', send_onboarding_email: false,
  });
  const [busy, setBusy] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState(null); // {api_key, name, ...} after POST
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await AdminAPI.listPartnerKeys();
      setKeys(Array.isArray(list) ? list : []);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les clés partenaires.' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Nom requis', description: 'Donnez un nom descriptif (ex : "Yassir Dental App").' });
      return;
    }
    setBusy(true);
    try {
      const res = await AdminAPI.createPartnerKey({
        name: form.name.trim(),
        scopes: form.scopes,
        rate_limit_per_minute: Number(form.rate_limit_per_minute) || 60,
        partner_email: form.send_onboarding_email ? form.partner_email.trim() : null,
        send_onboarding_email: !!form.send_onboarding_email && !!form.partner_email.trim(),
      });
      setNewlyCreated(res);
      setCreating(false);
      setForm({
        name: '', scopes: SCOPES_AVAILABLE.map(s => s.value), rate_limit_per_minute: 60,
        partner_email: '', send_onboarding_email: false,
      });
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible' });
    } finally { setBusy(false); }
  };

  const toggleActive = async (k) => {
    try {
      await AdminAPI.patchPartnerKey(k.id, { active: !k.active });
      setKeys(prev => prev.map(x => x.id === k.id ? { ...x, active: !x.active } : x));
    } catch {
      toast({ title: 'Erreur', description: 'Bascule impossible.' });
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Supprimer définitivement la clé "${k.name}" ? Le partenaire perdra l'accès immédiatement.`)) return;
    try {
      await AdminAPI.deletePartnerKey(k.id);
      toast({ title: '✅ Clé supprimée' });
      await reload();
    } catch {
      toast({ title: 'Erreur', description: 'Suppression impossible.' });
    }
  };

  const copyKey = async () => {
    if (!newlyCreated?.api_key) return;
    try {
      await navigator.clipboard.writeText(newlyCreated.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copie impossible', description: 'Copiez la clé manuellement.' });
    }
  };

  const toggleScope = (s) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s],
    }));
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>;

  return (
    <div data-testid="admin-partner-keys-tab" className="space-y-5">
      {/* Header */}
      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><Key className="w-4 h-4 text-[#1F4D3F]" /> Clés API Partenaires</h3>
          <p className="text-xs text-[#7A6F5C] mt-1 max-w-2xl">
            Donnez accès à votre catalogue à des partenaires externes (marketplaces, logiciels de gestion dentaire, comparateurs).
            Chaque clé est rate-limitée à 60 requêtes / minute. Endpoints : <code className="text-[#1F4D3F]">/api/partner/products</code>, <code className="text-[#1F4D3F]">/categories</code>, <code className="text-[#1F4D3F]">/brands</code>, <code className="text-[#1F4D3F]">/me</code>.
          </p>
        </div>
        <Button data-testid="partner-key-create-btn" onClick={() => setCreating(true)} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle clé
        </Button>
      </div>

      {/* Documentation */}
      <details className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl p-4 text-sm" data-testid="partner-key-docs">
        <summary className="cursor-pointer font-medium text-[#1F4D3F] flex items-center gap-2">
          <Code2 className="w-4 h-4" /> Documentation rapide pour le partenaire
        </summary>
        <div className="mt-3 space-y-2 text-[#5A5247]">
          <p>Chaque requête doit inclure le header <code className="bg-white px-1.5 py-0.5 rounded border border-[#E8E0D0]">X-API-Key: pk_xxxxxxxx</code>.</p>
          <pre className="bg-[#2D2A26] text-[#A8C5B5] rounded-lg p-3 overflow-x-auto text-xs">{`curl -H "X-API-Key: pk_xxxxxxxxxxxxx" \\
  ${typeof window !== 'undefined' ? window.location.origin : ''}/api/partner/products?limit=20`}</pre>
          <p className="text-xs">
            <strong>Réponse :</strong> <code>{`{ total, limit, offset, items: [...] }`}</code> · <strong>Params :</strong> <code>category</code>, <code>brand</code>, <code>q</code>, <code>limit</code> (1-200), <code>offset</code>.
          </p>
        </div>
      </details>

      {/* Keys list */}
      {keys.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8E0D0] rounded-2xl p-10 text-center" data-testid="partner-keys-empty">
          <Key className="w-10 h-10 text-[#C9BFAE] mx-auto mb-3" />
          <p className="text-sm text-[#7A6F5C]">Aucune clé partenaire. Créez-en une pour donner accès à votre catalogue.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden" data-testid="partner-keys-list">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Clé (préfixe)</th>
                <th className="px-4 py-3">Scopes</th>
                <th className="px-4 py-3">Requêtes</th>
                <th className="px-4 py-3">Dernière utilisation</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="border-t border-[#F5F1EA]" data-testid={`partner-key-row-${k.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#2D2A26]">{k.name}</div>
                    <div className="text-[10px] text-[#7A6F5C] mt-0.5">Créée le {new Date(k.created_at).toLocaleDateString('fr-DZ')}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#5A5247]">{k.key_prefix}…</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(k.scopes || []).map(s => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#E4EDE9] text-[#1F4D3F]">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#5A5247]">{(k.requests_count || 0).toLocaleString('fr-DZ')}</td>
                  <td className="px-4 py-3 text-xs text-[#7A6F5C]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString('fr-DZ') : '—'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => toggleActive(k)} title={k.active ? 'Désactiver' : 'Activer'}
                      data-testid={`partner-key-toggle-${k.id}`}
                      className={`mr-1 ${k.active ? 'text-emerald-600' : 'text-[#C9BFAE]'}`}>
                      {k.active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                    </button>
                    <button onClick={() => remove(k)} title="Supprimer"
                      data-testid={`partner-key-delete-${k.id}`}
                      className="p-1.5 hover:bg-rose-50 rounded text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4" data-testid="partner-key-create-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <h3 className="font-medium text-[#2D2A26]">Nouvelle clé partenaire</h3>
              <button onClick={() => setCreating(false)} className="text-[#7A6F5C] hover:text-[#2D2A26]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom du partenaire *</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex : Yassir Dental App, Marketplace Dentaire DZ…"
                  data-testid="partner-key-form-name" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Scopes (permissions)</label>
                <div className="space-y-1.5">
                  {SCOPES_AVAILABLE.map(s => (
                    <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.scopes.includes(s.value)}
                        onChange={() => toggleScope(s.value)}
                        className="w-4 h-4" data-testid={`partner-key-scope-${s.value}`} />
                      <span className="text-[#5A5247]">{s.label}</span>
                      <code className="text-[10px] text-[#7A6F5C] ml-auto">{s.value}</code>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Rate limit (req / minute)</label>
                <Input type="number" min="1" max="600" value={form.rate_limit_per_minute}
                  onChange={e => setForm({ ...form, rate_limit_per_minute: e.target.value })}
                  data-testid="partner-key-form-rate" />
                <p className="text-[11px] text-[#7A6F5C] mt-1">Défaut : 60 req/min · max : 600/min</p>
              </div>

              {/* Onboarding email — optional */}
              <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.send_onboarding_email}
                    onChange={e => setForm({ ...form, send_onboarding_email: e.target.checked })}
                    className="w-4 h-4" data-testid="partner-key-form-send-email" />
                  <span className="text-sm font-medium text-[#1F4D3F]">Envoyer un email d'onboarding au partenaire</span>
                </label>
                {form.send_onboarding_email && (
                  <div>
                    <label className="text-xs text-[#7A6F5C] mb-1 block">Email du partenaire *</label>
                    <Input type="email" value={form.partner_email}
                      onChange={e => setForm({ ...form, partner_email: e.target.value })}
                      placeholder="contact@partenaire.dz"
                      data-testid="partner-key-form-email" />
                    <p className="text-[11px] text-[#7A6F5C] mt-1">
                      L'email contiendra la clé en clair + un exemple curl + la liste des endpoints.
                      Si Resend n'est pas configuré, l'envoi est en mode <em>mocked</em> (loggé seulement).
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E8E0D0] bg-[#FAF7F2]">
              <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
              <Button onClick={create} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="partner-key-form-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Créer la clé
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Newly created key — display ONCE */}
      {newlyCreated && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4" data-testid="partner-key-reveal-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <h3 className="font-medium text-[#2D2A26] flex items-center gap-2">
                <Key className="w-4 h-4 text-emerald-600" /> Clé créée — {newlyCreated.name}
              </h3>
              <button onClick={() => setNewlyCreated(null)} className="text-[#7A6F5C] hover:text-[#2D2A26]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Cette clé est affichée UNE SEULE FOIS.</strong> Copiez-la maintenant dans votre gestionnaire de mots de passe et partagez-la avec votre partenaire via un canal sécurisé.
                  Vous ne pourrez plus la récupérer ensuite (seul le hash SHA-256 est conservé).
                </span>
              </div>
              <div>
                <label className="text-xs text-[#7A6F5C] mb-1.5 block">Clé API</label>
                <div className="flex gap-2">
                  <code className="flex-1 bg-[#2D2A26] text-[#A8C5B5] rounded-lg px-3 py-2.5 text-xs font-mono break-all select-all" data-testid="partner-key-revealed">
                    {newlyCreated.api_key}
                  </code>
                  <Button onClick={copyKey} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F] flex-shrink-0" data-testid="partner-key-copy-btn">
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <details className="text-xs text-[#5A5247]">
                <summary className="cursor-pointer text-[#1F4D3F] font-medium">Exemple d'usage curl</summary>
                <pre className="mt-2 bg-[#2D2A26] text-[#A8C5B5] rounded p-2 overflow-x-auto">{`curl -H "X-API-Key: ${newlyCreated.api_key}" \\
  ${typeof window !== 'undefined' ? window.location.origin : ''}/api/partner/me`}</pre>
              </details>

              {/* Onboarding email status */}
              {newlyCreated.email && (
                <div data-testid="partner-key-email-status" className={`rounded-lg p-3 text-xs flex items-start gap-2 ${
                  newlyCreated.email.ok
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                    : newlyCreated.email.mocked
                      ? 'bg-amber-50 border border-amber-200 text-amber-900'
                      : 'bg-rose-50 border border-rose-200 text-rose-900'
                }`}>
                  {newlyCreated.email.ok ? (
                    <><Check className="w-4 h-4 flex-shrink-0 mt-0.5" /> Email d'onboarding envoyé avec succès (id : <code>{newlyCreated.email.id}</code>)</>
                  ) : newlyCreated.email.mocked ? (
                    <><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Email d'onboarding en mode <strong>mocked</strong> — Resend n'est pas configuré. Configurez la clé dans <strong>Intégrations → Resend</strong> pour l'envoi réel.</>
                  ) : (
                    <><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Échec d'envoi : {newlyCreated.email.error || 'erreur inconnue'}. Vous pouvez transmettre la clé manuellement.</>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end px-5 py-3 border-t border-[#E8E0D0] bg-[#FAF7F2]">
              <Button onClick={() => setNewlyCreated(null)} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="partner-key-reveal-close">
                J'ai sauvegardé la clé
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerKeysTab;
